import { useCallback, useMemo } from 'react';
import { useQuery } from 'hooks/use-query';

import { useCatalogRequestContext } from 'src/contexts/catalog-request-context';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import { determinePlatformType } from 'shared/helpers/enum-mappings';
import { MenuPricingTypes, ProductsProvider } from 'src/gql/graphql';
import { parseProduct, Product } from 'utils/helpers/product';
import filteredProducts from 'shared/graphql/product/filtered-products.gql';
import type { FilteredProductsQuery, FilteredProductsQueryVariables } from 'src/gql/graphql';

type UseSerializeProductsFromIdsParams = {
  productIds: string[];
  excludeProducts?: string[];
  limit: number;
  skip?: boolean;
};

type UseSerializeProductsFromIdsReturn = {
  data?: Product[];
  error?: Error;
  loading: boolean;
};

export function useSerializeProductsFromIds({
  productIds,
  excludeProducts = [],
  limit,
  skip = false,
}: UseSerializeProductsFromIdsParams): UseSerializeProductsFromIdsReturn {
  const { dispensary, loading: dispensaryLoading } = useDispensary();
  const { menuType } = useCatalogRequestContext();
  const UI = useUI();
  const { isKiosk } = UI;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const productIdsMemo = useMemo(() => productIds, [JSON.stringify(productIds)]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const excludeProductsMemo = useMemo(() => excludeProducts, [JSON.stringify(excludeProducts)]);

  const getVariables = useCallback(() => {
    const dispensaryId = dispensary?.id;
    if (!dispensaryId) {
      return null;
    }

    return {
      productsFilter: {
        sortBy: 'inputOrder',
        bypassOnlineThresholds: isKiosk,
        dispensaryId,
        isKioskMenu: isKiosk,
        pricingType: menuType as MenuPricingTypes,
        Status: 'Active' as const,
        removeProductsBelowOptionThresholds: true,
        productIds: productIdsMemo,
        platformType: determinePlatformType(UI),
      },
      perPage: limit,
    };
  }, [dispensary, isKiosk, limit, menuType, productIdsMemo, UI]);

  const shouldSkipQuery = skip || dispensaryLoading || !dispensary?.id || !menuType || productIdsMemo.length === 0;

  const { data: queryData, loading: queryLoading, error: queryError } = useQuery<
    FilteredProductsQuery,
    FilteredProductsQueryVariables
  >(filteredProducts, {
    skip: shouldSkipQuery,
    variables: shouldSkipQuery ? undefined : getVariables() ?? undefined,
  });

  const excludeProductsSet = new Set(excludeProductsMemo);
  const filterProduct = ({ id }: Product): boolean => typeof id === 'string' && !excludeProductsSet.has(id);

  const processedData = queryData?.filteredProducts?.products
    ? queryData.filteredProducts.products
        .map((product) => ({ ...parseProduct(product), provider: ProductsProvider.dutchie }))
        .filter(filterProduct)
    : undefined;

  return {
    data: processedData,
    loading: queryLoading || dispensaryLoading,
    error: queryError ? new Error(queryError.message || 'Error fetching products') : undefined,
  };
}
